import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, TrendingUp, Calendar } from "lucide-react";
import { useAttendanceStats } from "@/hooks/admin/attendance/useAttendanceStats";
import { useWeeklyAttendanceStats } from "@/hooks/admin/attendance/useWeeklyAttendanceStats";
import { HourlyChart } from "./HourlyChart";
import { WeeklyChart } from "./WeeklyChart";
import { format, isToday, isSameDay } from "date-fns";
import { es } from "date-fns/locale";

interface AttendanceStatsProps {
  gymId: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export const AttendanceStats = ({ gymId, dateFrom, dateTo }: AttendanceStatsProps) => {
  const { data: stats, isLoading } = useAttendanceStats({ gymId, dateFrom, dateTo });
  const { data: weeklyStats, isLoading: isWeeklyLoading } = useWeeklyAttendanceStats({ gymId });

  const getStatsTitle = () => {
    if (!dateFrom || !dateTo) return "Ingresos Hoy";
    
    if (isSameDay(dateFrom, dateTo)) {
      if (isToday(dateFrom)) return "Ingresos Hoy";
      return `Ingresos del ${format(dateFrom, "dd/MM", { locale: es })}`;
    }
    
    return `Ingresos ${format(dateFrom, "dd/MM", { locale: es })} - ${format(dateTo, "dd/MM", { locale: es })}`;
  };

  const getChartTitle = () => {
    if (stats?.isMultipleDays) {
      return `Promedio por Hora (${stats.daysCount} días)`;
    }
    return "Distribución por Hora (8:00 - 23:00)";
  };

  if (isLoading || isWeeklyLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-16" />
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[150px] sm:h-[200px] w-full" />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader className="pb-2">
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[150px] sm:h-[200px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const peakHour = stats?.byHour.reduce((max, curr) => 
    curr.count > max.count ? curr : max
  , { hour: 0, count: 0 });

  return (
    <div className="space-y-4">
      {/* Top row: KPI + Hourly Chart */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* KPI Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {getStatsTitle()}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.totalCount || 0}</div>
            {peakHour && peakHour.count > 0 && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                {stats?.isMultipleDays ? "Pico promedio" : "Pico"}: {peakHour.hour}:00 ({peakHour.count})
              </p>
            )}
          </CardContent>
        </Card>

        {/* Hourly distribution chart */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {getChartTitle()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.byHour && <HourlyChart data={stats.byHour} />}
          </CardContent>
        </Card>
      </div>

      {/* Weekly Chart - Full width */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Asistencia Última Semana
          </CardTitle>
          {weeklyStats && (
            <span className="text-[11px] sm:text-xs text-muted-foreground">
              Total: {weeklyStats.totalWeek} | Prom: {weeklyStats.averagePerDay}/día
            </span>
          )}
        </CardHeader>
        <CardContent>
          {weeklyStats?.days && <WeeklyChart data={weeklyStats.days} />}
        </CardContent>
      </Card>
    </div>
  );
};
